import React, {useEffect, useState} from "react";
import {Routes, Route, useLocation} from "react-router-dom";
import DishDetailPage from "./DishDetailPage.jsx";
import MainPage from "./MainPage.jsx";
import OrderPage from "./OrderPage.jsx";
import CardPage from "./CardPage.jsx";
import {CartProvider} from "./context/CartContext";
import ReservationPage from "./ReservationPage.jsx";
import ReservationPreOrderPage from "./ReservationPreOrderPage.jsx";
import {RestaurantProvider} from "./context/RestaurantContext.jsx";
import {ToastContainer, toast} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OrdersHistory from "./OrdersHistory.jsx";
import OrderViewPage from "./OrderViewPage.jsx";
import ReservationEditPage from "./ReservationEditPage.jsx";
import appConfig from "./config";
import {UserProvider} from "./context/UserContext";
import PaymentPage from "./PaymentPage.jsx";
import PaymentProcessingPage from "./PaymentProcessingPage.jsx";
import OrderSuccessPage from "./OrderSuccessPage.jsx";
import OrderWaitingPage from "./OrderWaitingPage.jsx";
import ProfilePage from "./ProfilePage.jsx";
import AboutPage from "./AboutPage.jsx";
import BottomNav from "./components/BottomNav.jsx";
import {PaymentProvider} from "./context/PaymentContext.jsx";
import {syncPushSubscription} from "./hooks/usePushNotifications.js";

const isPwaMode = window.APP_CONFIG?.basePath === '/pwa';

// Страницы где показывается нижняя навигация
const NAV_PATHS = new Set(['/', '/history', '/profile']);

const BottomNavWrapper = () => {
    const { pathname } = useLocation();
    return NAV_PATHS.has(pathname) ? <BottomNav /> : null;
};

const App = () => {
    const [slug, setSlug] = useState(null);
    const [tableId, setTableId] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [restaurantData, setRestaurantData] = useState(null);
    const [installPrompt, setInstallPrompt] = useState(null);
    const [installDismissed, setInstallDismissed] = useState(false);
    const [userData, setUserData] = useState(null);

    // Логотип по предсказуемому пути — строим из URL синхронно, без API-запроса.
    // Версия берётся из localStorage (обновляется после каждой загрузки данных).
    const [logoUrl] = useState(() => {
        const s = new URLSearchParams(window.location.search).get('slug');
        if (!s) return null;
        const v = localStorage.getItem(`logo_v_${s}`) || '0';
        return `${window.location.origin}/uploads/logos/${s}.png?v=${v}`;
    });

    useEffect(() => {
        const handleError = (event) => {
            console.error("Global JS error:", event.error);
            throw event.error; // попадёт в ErrorBoundary
        };

        const handleRejection = (event) => {
            console.error("Unhandled promise rejection:", event.reason);
            throw event.reason; // попадёт в ErrorBoundary
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleRejection);

        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleRejection);
        };
    }, []);

    useEffect(() => {
        if (!isPwaMode) return;

        const handler = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    useEffect(() => {
        if (!appConfig.validate()) {
            setAuthError('Неверная конфигурация приложения');
            setIsLoading(false);
            return;
        }

        const initializeApp = async () => {
            try {
                // Получение slug из URL
                const params = new URLSearchParams(window.location.search);
                const restaurantSlug = params.get('slug');
                const tableIdParam = params.get('table_id');
                if (tableIdParam) setTableId(tableIdParam);

                if (restaurantSlug) {
                    setSlug(restaurantSlug);
                } else {
                    // Нет слага — режим каталога (PWA без конкретного ресторана)
                    setIsLoading(false);
                    return;
                }

                // 1. Проверяем есть ли валидный токен в localStorage
                const token = localStorage.getItem("token");
                const user = localStorage.getItem("user");
                if (token && isTokenValid(token) && user) {
                    console.log("Using existing valid token");
                    appConfig.setToken(token);
                    appConfig.setUser(user)
                    setUserData(JSON.parse(user));

                    // Загружаем данные ресторана отдельно
                    await loadRestaurantData(restaurantSlug);
                    setIsLoading(false);
                    return;
                }

                // 2. Если токена нет, используем Telegram авторизацию с получением ресторана
                await checkTelegramReady(restaurantSlug);

            } catch (error) {
                console.error('Initialization error:', error);
                setAuthError('Ошибка инициализации приложения');
                setIsLoading(false);
            }
        };

        initializeApp();
    }, []);

    // Устанавливаем контекст для логов при изменении ресторана или пользователя
    useEffect(() => {
        if (restaurantData || userData) {
            // Функция для установки контекста логов (должна быть определена в main.js)
            if (window.__setLogContext) {
                window.__setLogContext({
                    restaurantId: restaurantData?.id,
                    userId: userData?.id
                });
            }
        }
    }, [restaurantData, userData]);

    // Проверяем токен локально
    const isTokenValid = (token) => {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return false;

            const payload = JSON.parse(atob(parts[1]));
            const currentTime = Math.floor(Date.now() / 1000);

            if (payload.exp && payload.exp < currentTime) {
                localStorage.removeItem("token");
                return false;
            }

            return true;
        } catch (error) {
            return false;
        }
    };

    const checkTelegramReady = (restaurantSlug) => {
        // В браузере (PWA) Telegram WebApp отсутствует — сразу загружаем ресторан
        if (!window.Telegram) {
            loadRestaurantData(restaurantSlug).finally(() => setIsLoading(false));
            return;
        }

        // В Telegram контексте — ждём initData (макс. 10 попыток = 1 секунда)
        const maxAttempts = 10;
        let attempts = 0;

        const check = () => {
            attempts++;
            const tg = window.Telegram?.WebApp;

            if (tg?.initData) {
                authenticateUser(tg.initData, restaurantSlug);
            } else if (attempts >= maxAttempts) {
                console.warn('Telegram initData not available, loading restaurant without auth');
                loadRestaurantData(restaurantSlug).finally(() => setIsLoading(false));
            } else {
                setTimeout(check, 100);
            }
        };

        check();
    };

    const authenticateUser = async (initData, restaurantSlug) => {
        try {
            setIsLoading(true);

            const response = await fetch(`${appConfig.endpoint}/auth?restaurantSlug=${restaurantSlug}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({initData})
            });

            const data = await response.json();

            if (data.success) {
                // Сохраняем токен
                appConfig.setToken(data.token);
                appConfig.setUser(JSON.stringify(data.user));
                setUserData(data.user);

                if (data.restaurant) {
                    setRestaurantData(data.restaurant);
                    if (data.restaurant.settings?.logo_v) {
                        try { localStorage.setItem(`logo_v_${restaurantSlug}`, String(data.restaurant.settings.logo_v)); } catch {}
                    }
                } else {
                    await loadRestaurantData(restaurantSlug);
                }

            } else {
                console.error("Auth error:", data.error);
                await loadRestaurantData(restaurantSlug);
                toast.warning('Ограниченный режим: некоторые функции недоступны');
            }

        } catch (error) {
            console.error("Auth request failed:", error);
            await loadRestaurantData(restaurantSlug);
            toast.warning('Нет соединения с сервером');
        } finally {
            setIsLoading(false);
        }
    };

    const loadRestaurantData = async (restaurantSlug) => {
        try {
            const headers = {
                'Content-Type': 'application/json',
            };

            if (appConfig.token) {
                headers['Authorization'] = `Bearer ${appConfig.token}`;
            }

            const response = await fetch(`${appConfig.endpoint}/restaurant/${restaurantSlug}`, {
                headers: headers
            });

            if (response.ok) {
                const data = await response.json();
                setRestaurantData(data);
                if (data.settings?.logo_v) {
                    try { localStorage.setItem(`logo_v_${restaurantSlug}`, String(data.settings.logo_v)); } catch {}
                }
                syncPushSubscription();
                // Отправляем кастомный лог об успешной загрузке ресторана
                if (window.__sendCustomLog) {
                    window.__sendCustomLog('restaurant_loaded', {
                        restaurantId: data.id,
                        restaurantName: data.name
                    }, 'info');
                }
            } else {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error) {
            console.error("Failed to load restaurant data:", error);
            setAuthError('Не удалось загрузить данные ресторана');

            // Отправляем лог об ошибке
            if (window.__sendCustomLog) {
                window.__sendCustomLog('restaurant_load_error', {
                    error: error.message,
                    slug: restaurantSlug
                }, 'error');
            }
        }
    };

    const retryAuth = () => {
        setAuthError(null);
        setIsLoading(true);

        // Отправляем лог о повторной попытке
        if (window.__sendCustomLog) {
            window.__sendCustomLog('auth_retry', { slug }, 'info');
        }

        if (window.Telegram?.WebApp?.initData && slug) {
            authenticateUser(window.Telegram.WebApp.initData, slug);
        } else {
            window.location.reload();
        }
    };

    if (authError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">Ошибка</h2>
                        <p className="text-gray-600 mb-6">{authError}</p>
                        <div className="space-y-3">
                            <button
                                onClick={retryAuth}
                                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                            >
                                Попробовать снова
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors"
                            >
                                Перезагрузить страницу
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Режим каталога — slug не передан (PWA установлено как платформа)
    if (!isLoading && !slug) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 px-6">
                <div className="text-center max-w-xs">
                    <div className="text-5xl mb-4">🍽️</div>
                    <h1 className="text-xl font-semibold text-gray-900 mb-2">Рестораны города</h1>
                    <p className="text-sm text-gray-500">Каталог ресторанов скоро появится здесь.<br/>Пока откройте приложение через QR-код в заведении.</p>
                </div>
            </div>
        );
    }

    if (isLoading || !slug || !restaurantData) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt=""
                        className="h-32 w-auto max-w-[260px] object-contain"
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                ) : (
                    <div className="w-32 h-32 rounded-2xl bg-gray-200 animate-pulse" />
                )}
                <div className="flex gap-1.5">
                    {[0, 1, 2].map(i => (
                        <div
                            key={i}
                            className="w-1.5 h-1.5 rounded-full bg-brandBlue/60 animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <UserProvider userData={userData}>
            <RestaurantProvider restaurantData={restaurantData} slug={slug}>
                <PaymentProvider>
                    <CartProvider key={slug} slug={slug} initialTableId={tableId}>
                        <Routes>
                            <Route path="/" element={<MainPage/>}/>
                            <Route path="/dish" element={<DishDetailPage/>}/>
                            <Route path="/order" element={<OrderPage/>}/>
                            <Route path="/cart" element={<CardPage/>}/>
                            <Route path="/reservation" element={<ReservationPage/>}/>
                            <Route path="/reservation-preorder" element={<ReservationPreOrderPage/>}/>
                            <Route path="/history" element={<OrdersHistory/>}/>
                            <Route path="/profile" element={<ProfilePage/>}/>
                            <Route path="/about" element={<AboutPage/>}/>
                            <Route path="/order/:orderId" element={<OrderViewPage/>}/>
                            <Route path="/reservation/:reservationId" element={<ReservationEditPage/>}/>
                            <Route path="/payment" element={<PaymentPage/>}/>
                            <Route path="/payment-processing" element={<PaymentProcessingPage/>}/>
                            <Route path="/order-success" element={<OrderSuccessPage/>}/>
                            <Route path="/order-waiting" element={<OrderWaitingPage/>}/>
                        </Routes>
                        <BottomNavWrapper />

                        {installPrompt && !installDismissed && (
                            <div className="fixed bottom-20 left-4 right-4 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 flex items-center gap-3">
                                <div className="text-2xl">📱</div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900">Добавить на экран</p>
                                    <p className="text-xs text-gray-500">Быстрый доступ без браузера</p>
                                </div>
                                <button
                                    onClick={() => {
                                        installPrompt.prompt();
                                        installPrompt.userChoice.then(() => setInstallPrompt(null));
                                    }}
                                    className="bg-brandBlue text-white text-sm font-medium px-3 py-1.5 rounded-lg shrink-0"
                                >
                                    Установить
                                </button>
                                <button
                                    onClick={() => setInstallDismissed(true)}
                                    className="text-gray-400 hover:text-gray-600 shrink-0 text-lg leading-none"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        <ToastContainer
                            position="top-center"
                            autoClose={3000}
                            hideProgressBar={false}
                            newestOnTop={true}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            theme="light"
                        />
                    </CartProvider>
                </PaymentProvider>
            </RestaurantProvider>
        </UserProvider>
    );
};

export default App;